import { Injectable } from '@angular/core';
import { TinterAddition } from '../../models/tinter-addition.model';
import {IFdgData, Iline} from "../../models/chart-fdg.model";

/**
 * Service responsible for mapping tinter additions to their corresponding measurement dates.
 */
@Injectable({
  providedIn: 'root',
})
export class CorrespondingTinterAdditionsService {
  constructor() {}

  /**
   * Returns structured array of tinter additions grouped by corresponding dates.
   * Index of returned array has same value as index of given dates array.
   */
  public getTinterAdditionsCorrespondingToDates(
    startDateString: string,
    fdgData: IFdgData,
    tinterAdditions: TinterAddition[],
  ): TinterAddition[][] {
    if (!fdgData || !fdgData.data || !fdgData.dates) {
      return [];
    }

    const dateRange = fdgData.dates;
    if (dateRange == null || dateRange.length === 0 || tinterAdditions == null || tinterAdditions.length === 0) {
      return [];
    }

    const relevantTinterAdditions: TinterAddition[] = tinterAdditions.filter(
      ta => {
        if (ta != null && ta.additionDate != null) {
          // BCH-608: Tinting is only relevant if it has been added after the start date
          const additionDate = new Date(ta.additionDate);
          const startDate = new Date(startDateString);
          if (additionDate.getTime() >= startDate.getTime()) {
            return ta;
          }
        }
      }
    );

    if (relevantTinterAdditions.length === 0) {
      return [];
    }
    return this._getTinterAdditionsCorrespondingToDates(fdgData, relevantTinterAdditions);
  }

  private _getTinterAdditionsCorrespondingToDates(
    fdgData: IFdgData,
    relevantTinterAdditions: TinterAddition[],
  ): TinterAddition[][] {
    const correspondingTinterAdditions: TinterAddition[][] = [];

    relevantTinterAdditions.forEach(ta => {
      const closestIndex = this.findClosestIndex(ta.additionDate, fdgData.dates);
      // #508837 Update tinter adds display for FDG plot to verify with batch line key
      if (this.containsBatchLineKey(closestIndex, ta.batchId + '-' + ta.line, fdgData)) {
        this.putInArrayAt(correspondingTinterAdditions, ta, closestIndex);
      }
    });
    return correspondingTinterAdditions;
  }

  // #508837 Update tinter adds display for FDG plot
  private containsBatchLineKey(index: number, batchLineKey: string, fdgData: IFdgData): boolean {
    let contains = false;
    // iterate over different deltas and check if at least one component of that batch line key is present for that fdg date index
    for (const DX of Object.keys(fdgData.data)) {
      if (fdgData.data[DX].dataSets) {
        const currentData: Iline[] = fdgData.data[DX].dataSets[index];
        contains = (currentData && currentData.some(item => item.batchLineKey === batchLineKey));
        if (contains) {
          break;
        }
      }
    }
    return contains;
  }

  private findClosestIndex(searchedDateString: string, dateRange: string[]): number {
    const searchedDate = new Date(searchedDateString);

    for (let i = 0; i < dateRange.length; i++) {
      const dateFromRange = new Date(dateRange[i]);
      if (searchedDate.getTime() <= dateFromRange.getTime()) {
        return i;
      }
    }

    return -1;
  }

  private putInArrayAt(array: TinterAddition[][], tinterAddition2Add: TinterAddition, index: number): void {
    if (index < 0) {
      return;
    }
    if (array[index] == null) {
      array[index] = [];
    }
    array[index].push(tinterAddition2Add);
  }
}
